import axios from 'axios'
import { config } from '../../env.config.js'
import logger from '../utils/logger'

const API_BASE_URL = config.API_URL

// Crear instancia de axios
const api = axios.create({
      baseURL: API_BASE_URL,
  timeout: 120000, // Aumentado a 120 segundos para consultas grandes
      headers: {
        'Content-Type': 'application/json',
      },
    })

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
      (config) => {
    const token = localStorage.getItem('authToken')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
          logger.debug('Token agregado al request:', token.substring(0, 30) + '...')
        } else {
          logger.debug('No hay token en localStorage')
        }
        
        // Debug: mostrar todos los headers
        logger.debug('Headers del request:', config.headers)
        
        if (config.DEBUG) {
          logger.api('API Request:', config.method?.toUpperCase(), config.url, config.data)
        }
        return config
      },
  (error) => {
    if (config.DEBUG) {
      logger.error('API Request Error:', error)
    }
    return Promise.reject(error)
  }
)

// Interceptor para manejar respuestas
api.interceptors.response.use(
      (response) => {
    if (config.DEBUG) {
      logger.api('API Response:', response.status, response.data)
    }
        return response
      },
      (error) => {
    if (config.DEBUG) {
      logger.error('API Response Error:', error.response?.status, error.response?.data)
    }
    
    // Manejar errores de autenticación
        if (error.response?.status === 401) {
      localStorage.removeItem('authToken')
          window.location.href = '/login'
        }
    
        return Promise.reject(error)
      }
    )

const apiService = {
  // Autenticación
  loginWithGoogle: async (googleData) => {
    try {
      const response = await api.post('/auth/google', {
        access_token: googleData.access_token,
        user_info: googleData.user_info
      })
      return response
    } catch (error) {
      logger.error('Error en loginWithGoogle:', error)
      throw error
    }
  },

  loginLocal: async (credentials) => {
    try {
      const response = await api.post('/auth/local/login', credentials)
      return response
    } catch (error) {
      logger.error('Error en loginLocal:', error)
      throw error
    }
  },

  registerLocal: async (userData) => {
    try {
      const response = await api.post('/auth/local/register', userData)
      return response
    } catch (error) {
      console.error('Error en registerLocal:', error)
      throw error
    }
  },

  resetPassword: async (resetData) => {
    try {
      const response = await api.post('/auth/local/reset-password', resetData)
      return response
    } catch (error) {
      console.error('Error en resetPassword:', error)
      throw error
    }
  },

  loginWithDevToken: async (token) => {
    try {
      const response = await api.post('/auth/dev', { token })
      return response
    } catch (error) {
      console.error('Error en loginWithDevToken:', error)
      throw error
    }
  },

  logout: async () => {
    try {
      const response = await api.post('/auth/logout')
      return response
    } catch (error) {
      console.error('Error en logout:', error)
      throw error
    }
  },

  // Perfil de usuario
  getProfile: async () => {
    try {
      const response = await api.get('/auth/local/me')
      return response
    } catch (error) {
      console.error('Error en getProfile:', error)
      throw error
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData)
      return response
    } catch (error) {
      console.error('Error en updateProfile:', error)
      throw error
    }
  },

  // Dashboard
  getDashboardStats: async (params = {}) => {
    try {
      const response = await api.get('/dashboard/stats', { params })
      return response
    } catch (error) {
      console.error('Error en getDashboardStats:', error)
      throw error
    }
  },

  getRecentOrders: async (params = {}) => {
    try {
      const response = await api.get('/dashboard/recent-orders', { params })
      return response
    } catch (error) {
      console.error('Error en getRecentOrders:', error)
      throw error
    }
  },

  getClientsMarketplace: async (params = {}) => {
    try {
      const response = await api.get('/dashboard/clients-marketplace', { params })
      return response
    } catch (error) {
      console.error('Error en getClientsMarketplace:', error)
      throw error
    }
  },

  getActivityChart: async (params = {}) => {
    try {
      const response = await api.get('/dashboard/activity-chart', { 
        params,
        timeout: 45000 // Timeout específico más largo para consultas complejas
      })
      return response
    } catch (error) {
      console.error('Error en getActivityChart:', error)
      throw error
    }
  },

  // Pedidos
  getOrders: async (params = {}) => {
    try {
      const response = await api.get('/orders', { params })
      return response
    } catch (error) {
      console.error('Error en getOrders:', error)
      throw error
    }
  },

  getOrderCompanies: async () => {
    try {
      const response = await api.get('/orders/companies')
      return response
    } catch (error) {
      console.error('Error en getOrderCompanies:', error)
      throw error
    }
  },

  getOrder: async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`)
      return response
    } catch (error) {
      console.error('Error en getOrder:', error)
      throw error
    }
  },

  createOrder: async (orderData) => {
    try {
      const response = await api.post('/orders', orderData)
      return response
    } catch (error) {
      console.error('Error en createOrder:', error)
      throw error
    }
  },

  updateOrder: async (orderId, orderData) => {
    try {
      const response = await api.put(`/orders/${orderId}`, orderData)
      return response
    } catch (error) {
      console.error('Error en updateOrder:', error)
      throw error
    }
  },

  deleteOrder: async (orderId) => {
    try {
      const response = await api.delete(`/orders/${orderId}`)
      return response
    } catch (error) {
      console.error('Error en deleteOrder:', error)
      throw error
    }
  },

  // Clientes
  getCustomers: async (params = {}) => {
    try {
      const response = await api.get('/customers', { params })
      return response
    } catch (error) {
      console.error('Error en getCustomers:', error)
      throw error
    }
  },

  getCustomer: async (customerId) => {
    try {
      const response = await api.get(`/customers/${customerId}`)
      return response
    } catch (error) {
      console.error('Error en getCustomer:', error)
      throw error
    }
  },

  createCustomer: async (customerData) => {
    try {
      const response = await api.post('/customers', customerData)
      return response
    } catch (error) {
      console.error('Error en createCustomer:', error)
      throw error
    }
  },

  updateCustomer: async (customerId, customerData) => {
    try {
      const response = await api.put(`/customers/${customerId}`, customerData)
      return response
    } catch (error) {
      console.error('Error en updateCustomer:', error)
      throw error
    }
  },

  deleteCustomer: async (customerId) => {
    try {
      const response = await api.delete(`/customers/${customerId}`)
      return response
    } catch (error) {
      console.error('Error en deleteCustomer:', error)
      throw error
    }
  },

  // Empresas
  getCompanies: async (params = {}) => {
    try {
      const response = await api.get('/companies', { params })
      return response
    } catch (error) {
      console.error('Error en getCompanies:', error)
      throw error
    }
  },

  getCompany: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}`)
      return response
    } catch (error) {
      console.error('Error en getCompany:', error)
      throw error
    }
  },

  createCompany: async (companyData) => {
    try {
      const response = await api.post('/companies', companyData)
      return response
    } catch (error) {
      console.error('Error en createCompany:', error)
      throw error
    }
  },

  updateCompany: async (companyId, companyData) => {
    try {
      const response = await api.put(`/companies/${companyId}`, companyData)
      return response
    } catch (error) {
      console.error('Error en updateCompany:', error)
      throw error
    }
  },

  deleteCompany: async (companyId) => {
    try {
      const response = await api.delete(`/companies/${companyId}`)
      return response
    } catch (error) {
      console.error('Error en deleteCompany:', error)
      throw error
    }
  },

  // Reportes
  getReports: async (params = {}) => {
    try {
      const response = await api.get('/reports', { params })
      return response
    } catch (error) {
      console.error('Error en getReports:', error)
      throw error
    }
  },

  generateReport: async (reportData) => {
    try {
      const response = await api.post('/reports/generate', reportData)
      return response
    } catch (error) {
      console.error('Error en generateReport:', error)
      throw error
    }
  },

  // Configuración
  getSettings: async () => {
    try {
      const response = await api.get('/settings')
      return response
    } catch (error) {
      console.error('Error en getSettings:', error)
      throw error
    }
  },

  updateSettings: async (settingsData) => {
    try {
      const response = await api.put('/settings', settingsData)
      return response
    } catch (error) {
      console.error('Error en updateSettings:', error)
      throw error
    }
  },

  // Reportes
  getSalesReport: async (params) => {
    try {
      const response = await api.get('/reports/sales-test', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getSalesReport:', error)
      throw error
    }
  },
  getClientPerformanceReport: async (params) => {
    try {
      const response = await api.get('/reports/clients-test', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getClientPerformanceReport:', error)
      throw error
    }
  },
  getMarketplaceReport: async (params) => {
    try {
      const response = await api.get('/reports/marketplaces-test', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getMarketplaceReport:', error)
      throw error
    }
  },
  getMarketplaceDailySales: async (params) => {
    try {
      // Timeout específico más largo para esta consulta que puede ser pesada
      const response = await api.get('/reports/marketplace-daily-sales', { 
        params,
        timeout: 180000 // 3 minutos para consultas de fechas amplias
      })
      return response.data || response
    } catch (error) {
      console.error('Error en getMarketplaceDailySales:', error)
      throw error
    }
  },
  getProductsAnalysis: async (params) => {
    try {
      const response = await api.get('/reports/products-analysis', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getProductsAnalysis:', error)
      throw error
    }
  },
  
  // Obtener reporte de productos con crecimiento
  getProductsGrowthReport: async (params) => {
    try {
      const response = await api.get('/reports/products-growth', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getProductsGrowthReport:', error)
      throw error
    }
  },
  
  // Obtener reporte de productos con disminución
  getProductsDeclineReport: async (params) => {
    try {
      const response = await api.get('/reports/products-decline', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getProductsDeclineReport:', error)
      throw error
    }
  },

  // =============================
  // ANÁLISIS DE TENDENCIAS
  // =============================

  // Obtener tendencias de productos (crecimiento/decrecimiento)
  getProductTrends: async (params) => {
    try {
      const response = await api.get('/trends/products', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getProductTrends:', error)
      throw error
    }
  },

  // Obtener tendencias de ventas con detección de anomalías
  getSalesTrends: async (params) => {
    try {
      const response = await api.get('/trends/sales', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getSalesTrends:', error)
      throw error
    }
  },

  // Obtener tendencias de marketplaces
  getMarketplaceTrends: async (params) => {
    try {
      const response = await api.get('/trends/marketplaces', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getMarketplaceTrends:', error)
      throw error
    }
  },

  // Obtener resumen de tendencias
  getTrendsSummary: async (params) => {
    try {
      const response = await api.get('/trends/summary', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getTrendsSummary:', error)
      throw error
    }
  },

  // =============================
  // EXECUTIVE DASHBOARD
  // =============================

  // Obtener resumen ejecutivo
  getExecutiveOverview: async (params) => {
    try {
      const response = await api.get('/executive/overview', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getExecutiveOverview:', error)
      throw error
    }
  },

  // Obtener índice de salud del negocio
  getBusinessHealthScore: async (params) => {
    try {
      const response = await api.get('/executive/health-score', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getBusinessHealthScore:', error)
      throw error
    }
  },

  // Obtener insights ejecutivos
  getTopInsights: async (params) => {
    try {
      const response = await api.get('/executive/top-insights', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getTopInsights:', error)
      throw error
    }
  },

  getProvincesReport: async (params) => {
    try {
      const response = await api.get('/reports/provinces-test', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getProvincesReport:', error)
      throw error
    }
  },
  getCountriesReport: async (params) => {
    try {
      const response = await api.get('/reports/countries-test', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getCountriesReport:', error)
      throw error
    }
  },
  getCommissionReport: async (params) => {
    try {
      const response = await api.get('/reports/commissions-test', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getCommissionReport:', error)
      throw error
    }
  },

  // Obtener reporte de reembolsos
  getRefundsReport: async (params) => {
    try {
      const response = await api.get('/reports/refunds-report', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getRefundsReport:', error)
      throw error
    }
  },

  // Obtener lista de clientes para filtros
  getClientsList: async () => {
    try {
      const response = await api.get('/reports/clients-list-test')
      return response.data || response
    } catch (error) {
      console.error('Error en getClientsList:', error)
      throw error
    }
  },

  // ==================== GESTIÓN DE EMPRESAS ZENTRIK_CRM ====================

  // Obtener lista de empresas
  getCompaniesCRM: async (params = {}) => {
    try {
      const response = await api.get('/companies-crm', { params })
      return response
    } catch (error) {
      console.error('Error en getCompaniesCRM:', error)
      throw error
    }
  },

  // Obtener empresa por ID
  getCompanyCRM: async (companyId) => {
    try {
      const response = await api.get(`/companies-crm/${companyId}`)
      return response
    } catch (error) {
      console.error('Error en getCompanyCRM:', error)
      throw error
    }
  },

  // Crear nueva empresa
  createCompanyCRM: async (companyData) => {
    try {
      const response = await api.post('/companies-crm', companyData)
      return response
    } catch (error) {
      console.error('Error en createCompanyCRM:', error)
      throw error
    }
  },

  // Actualizar empresa
  updateCompanyCRM: async (companyId, companyData) => {
    try {
      const response = await api.put(`/companies-crm/${companyId}`, companyData)
      return response
    } catch (error) {
      console.error('Error en updateCompanyCRM:', error)
      throw error
    }
  },

  // Eliminar empresa
  deleteCompanyCRM: async (companyId) => {
    try {
      const response = await api.delete(`/companies-crm/${companyId}`)
      return response
    } catch (error) {
      console.error('Error en deleteCompanyCRM:', error)
      throw error
    }
  },

  // Obtener estadísticas de empresas
  getCompaniesStatsCRM: async () => {
    try {
      const response = await api.get('/companies-crm/stats/summary')
      return response
    } catch (error) {
      console.error('Error en getCompaniesStatsCRM:', error)
      throw error
    }
  },

  // ==================== MAPEO CON OKARION ====================

  // Obtener empresas de Okarion para mapeo
  getOkarionCompaniesForMapping: async (params = {}) => {
    try {
      const response = await api.get('/companies-crm/mapping/okarion-companies', { params })
      return response
    } catch (error) {
      console.error('Error en getOkarionCompaniesForMapping:', error)
      throw error
    }
  },

  // Buscar coincidencia en Okarion por nombre
  findOkarionMatch: async (companyName) => {
    try {
      const response = await api.get(`/companies-crm/mapping/find-match/${encodeURIComponent(companyName)}`)
      return response
    } catch (error) {
      console.error('Error en findOkarionMatch:', error)
      throw error
    }
  },

  // Obtener sugerencias de mapeo para una empresa
  getMappingSuggestions: async (zentrikCompanyId) => {
    try {
      const response = await api.get(`/companies-crm/mapping/suggestions/${zentrikCompanyId}`)
      return response
    } catch (error) {
      console.error('Error en getMappingSuggestions:', error)
      throw error
    }
  },

  // Obtener estado del mapeo
  getMappingStatus: async () => {
    try {
      const response = await api.get('/companies-crm/mapping/status')
      return response
    } catch (error) {
      console.error('Error en getMappingStatus:', error)
      throw error
    }
  },

  // Buscar empresas en Okarion
  searchOkarionCompanies: async (searchTerm) => {
    try {
      const response = await api.get('/companies-crm/mapping/okarion-companies', {
        params: { search: searchTerm }
      })
      return response
    } catch (error) {
      console.error('Error en searchOkarionCompanies:', error)
      throw error
    }
  },

  // Mapear empresas
  mapCompanies: async (zentrikCompanyId, okarionCompanyId, okarionCompanyName) => {
    try {
      const response = await api.post('/companies-crm/mapping/map', {
        zentrik_company_id: zentrikCompanyId,
        okarion_company_id: okarionCompanyId,
        okarion_company_name: okarionCompanyName
      })
      return response
    } catch (error) {
      console.error('Error en mapCompanies:', error)
      throw error
    }
  },

  // ==================== COMISIONES ====================

  // Obtener marketplaces disponibles
  getMarketplaces: async () => {
    try {
      const response = await api.get('/reports/marketplaces-list-test')
      return response.data || response
    } catch (error) {
      console.error('Error en getMarketplaces:', error)
      throw error
    }
  },

  // Obtener países disponibles
  getCountries: async () => {
    try {
      const response = await api.get('/commissions/countries')
      return response
    } catch (error) {
      console.error('Error en getCountries:', error)
      throw error
    }
  },

  // Obtener comisiones de una empresa
  getCompanyCommissions: async (companyId) => {
    try {
      const response = await api.get(`/commissions/company/${companyId}`)
      return response
    } catch (error) {
      console.error('Error en getCompanyCommissions:', error)
      throw error
    }
  },

  // Actualizar comisiones de una empresa
  updateCompanyCommissions: async (companyId, commissionData) => {
    try {
      const response = await api.put(`/commissions/company/${companyId}`, commissionData)
      return response
    } catch (error) {
      console.error('Error en updateCompanyCommissions:', error)
      throw error
    }
  },

  // Eliminar una comisión específica por marketplace
  deleteMarketplaceCommission: async (commissionId) => {
    try {
      const response = await api.delete(`/commissions/marketplace-commission/${commissionId}`)
      return response
    } catch (error) {
      console.error('Error en deleteMarketplaceCommission:', error)
      throw error
    }
  },

  // Obtener resumen de comisiones de una empresa
  getCompanyCommissionsSummary: async (companyId) => {
    try {
      const response = await api.get(`/commissions/company/${companyId}/summary`)
      return response
    } catch (error) {
      console.error('Error en getCompanyCommissionsSummary:', error)
      throw error
    }
  },

  // ==================== COMISIONES POR FRANJAS ====================

  // Crear comisión por franjas
  createTieredCommission: async (commissionData) => {
    try {
      const response = await api.post('/commissions/tiered-commission', commissionData)
      return response
    } catch (error) {
      console.error('Error en createTieredCommission:', error)
      throw error
    }
  },

  // Obtener comisión por franjas específica
  getTieredCommission: async (commissionId) => {
    try {
      const response = await api.get(`/commissions/tiered-commission/${commissionId}`)
      return response
    } catch (error) {
      console.error('Error en getTieredCommission:', error)
      throw error
    }
  },

  // Actualizar comisión por franjas
  updateTieredCommission: async (commissionId, commissionData) => {
    try {
      const response = await api.put(`/commissions/tiered-commission/${commissionId}`, commissionData)
      return response
    } catch (error) {
      console.error('Error en updateTieredCommission:', error)
      throw error
    }
  },

  // Eliminar comisión por franjas
  deleteTieredCommission: async (commissionId) => {
    try {
      const response = await api.delete(`/commissions/tiered-commission/${commissionId}`)
      return response
    } catch (error) {
      console.error('Error en deleteTieredCommission:', error)
      throw error
    }
  },

  // Validar configuración de franjas
  validateTierConfiguration: async (tiers) => {
    try {
      const response = await api.post('/commissions/validate-tiers', tiers)
      return response
    } catch (error) {
      console.error('Error en validateTierConfiguration:', error)
      throw error
    }
  },

  // ==================== USUARIOS ====================

  // Obtener lista de usuarios
  getUsers: async () => {
    try {
      const response = await api.get('/users/')
      return response
    } catch (error) {
      console.error('Error en getUsers:', error)
      throw error
    }
  },

  // Obtener usuario específico
  getUser: async (userId) => {
    try {
      const response = await api.get(`/users/${userId}`)
      return response
    } catch (error) {
      console.error('Error en getUser:', error)
      throw error
    }
  },

  // Crear nuevo usuario
  createUser: async (userData) => {
    try {
      const response = await api.post('/users/', userData)
      return response
    } catch (error) {
      console.error('Error en createUser:', error)
      throw error
    }
  },

  // Actualizar usuario
  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/users/${userId}`, userData)
      return response
    } catch (error) {
      console.error('Error en updateUser:', error)
      throw error
    }
  },

  // Eliminar usuario
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/users/${userId}`)
      return response
    } catch (error) {
      console.error('Error en deleteUser:', error)
      throw error
    }
  },

  // ==================== USUARIOS DE CUENTAS ====================

  // Obtener lista de usuarios de cuentas
  getAccountsUsers: async () => {
    try {
      const response = await api.get('/accounts-users/')
      return response
    } catch (error) {
      console.error('Error en getAccountsUsers:', error)
      throw error
    }
  },

  // Crear nuevo usuario de cuentas
  createAccountsUser: async (userData) => {
    try {
      const response = await api.post('/accounts-users/', userData)
      return response
    } catch (error) {
      console.error('Error en createAccountsUser:', error)
      throw error
    }
  },

  // Asignar empresas a usuario de cuentas
  assignCompaniesToUser: async (userId, companyIds) => {
    try {
      const response = await api.put(`/accounts-users/${userId}/assign-companies`, companyIds)
      return response
    } catch (error) {
      console.error('Error en assignCompaniesToUser:', error)
      throw error
    }
  },

  // Obtener empresas asignadas a un usuario
  getUserAssignedCompanies: async (userId) => {
    try {
      const response = await api.get(`/accounts-users/${userId}/assigned-companies`)
      return response
    } catch (error) {
      console.error('Error en getUserAssignedCompanies:', error)
      throw error
    }
  },

  // Obtener estadísticas de usuarios de cuentas
  getAccountsUsersStats: async () => {
    try {
      const response = await api.get('/accounts-users/stats/overview')
      return response
    } catch (error) {
      console.error('Error en getAccountsUsersStats:', error)
      throw error
    }
  },

  // Obtener lista de empresas para asignar a usuarios
  getCompaniesForUsers: async () => {
    try {
      const response = await api.get('/users/companies/list')
      return response
    } catch (error) {
      console.error('Error en getCompaniesForUsers:', error)
      throw error
    }
  },

  // ==================== FACTURACIÓN ====================

  // Obtener pedidos de facturación
  getFacturacionOrders: async (params = {}) => {
    try {
      const response = await api.get('/facturacion/orders', { params })
      return response
    } catch (error) {
      console.error('Error en getFacturacionOrders:', error)
      throw error
    }
  },

  // Obtener resumen de facturación
  getFacturacionSummary: async (params = {}) => {
    try {
      const response = await api.get('/facturacion/orders/summary', { params })
      return response
    } catch (error) {
      console.error('Error en getFacturacionSummary:', error)
      throw error
    }
  },

  // Obtener empresas de facturación
  getFacturacionCompanies: async (params = {}) => {
    try {
      const response = await api.get('/facturacion/companies', { params })
      return response
    } catch (error) {
      console.error('Error en getFacturacionCompanies:', error)
      throw error
    }
  },

  // Obtener cuotas por empresa de facturación
  getFacturacionCompaniesFees: async (params = {}) => {
    try {
      const response = await api.get('/facturacion/companies/fees', { params })
      return response
    } catch (error) {
      console.error('Error en getFacturacionCompaniesFees:', error)
      throw error
    }
  },

  // Obtener datos contables detallados para facturación
  getFacturacionAccounting: async (params = {}) => {
    try {
      const response = await api.get('/facturacion/accounting', { params })
      return response
    } catch (error) {
      console.error('Error en getFacturacionAccounting:', error)
      throw error
    }
  },

  // Guardar informe de contabilidad
  saveAccountingReport: async (reportData) => {
    try {
      const response = await api.post('/facturacion/save-accounting-report', reportData)
      return response
    } catch (error) {
      console.error('Error en saveAccountingReport:', error)
      throw error
    }
  },

  // Obtener informe de contabilidad existente
  getAccountingReport: async (companyId, startDate, endDate) => {
    try {
      const response = await api.get(`/facturacion/accounting-report/${companyId}/${startDate}/${endDate}`)
      return response
    } catch (error) {
      console.error('Error en getAccountingReport:', error)
      throw error
    }
  },

  // Listar todos los informes de contabilidad
  listAccountingReports: async (params = {}) => {
    try {
      const response = await api.get('/facturacion/accounting-reports', { params })
      return response
    } catch (error) {
      console.error('Error en listAccountingReports:', error)
      throw error
    }
  },

  // Eliminar informe de contabilidad
  deleteAccountingReport: async (reportId) => {
    try {
      const response = await api.delete(`/facturacion/accounting-report/${reportId}`)
      return response
    } catch (error) {
      console.error('Error en deleteAccountingReport:', error)
      throw error
    }
  },

  // Actualizar estado del informe de contabilidad
  updateAccountingReportStatus: async (reportId, newStatus, userEmail) => {
    try {
      const response = await api.patch(`/facturacion/accounting-report/${reportId}/status`, { 
        status: newStatus,
        user_email: userEmail 
      })
      return response
    } catch (error) {
      console.error('Error en updateAccountingReportStatus:', error)
      throw error
    }
  },

  // Obtener historial de auditoría de un informe
  getAccountingReportAuditLogs: async (reportId) => {
    try {
      const response = await api.get(`/facturacion/accounting-report/${reportId}/audit-logs`)
      return response
    } catch (error) {
      console.error('Error en getAccountingReportAuditLogs:', error)
      throw error
    }
  },

  // Obtener marketplaces de facturación
  getFacturacionMarketplaces: async (params = {}) => {
    try {
      const response = await api.get('/facturacion/marketplaces', { params })
      return response
    } catch (error) {
      console.error('Error en getFacturacionMarketplaces:', error)
      throw error
    }
  },

  // Obtener resumen de marketplace específico
  getFacturacionMarketplaceSummary: async (marketplaceName) => {
    try {
      const response = await api.get(`/facturacion/marketplaces/${encodeURIComponent(marketplaceName)}/summary`)
      return response
    } catch (error) {
      console.error('Error en getFacturacionMarketplaceSummary:', error)
      throw error
    }
  },

  // Obtener comisiones por marketplace
  getFacturacionMarketplacesCommissions: async (params = {}) => {
    try {
      const response = await api.get('/facturacion/marketplaces/commissions', { params })
      return response
    } catch (error) {
      console.error('Error en getFacturacionMarketplacesCommissions:', error)
      throw error
    }
  },

  // Obtener resumen de empresa específica
  getFacturacionCompanySummary: async (companyName) => {
    try {
      const response = await api.get(`/facturacion/companies/${encodeURIComponent(companyName)}/summary`)
      return response
    } catch (error) {
      console.error('Error en getFacturacionCompanySummary:', error)
      throw error
    }
  },

  // ==================== MARKETPLACE CAMPAIGNS ====================

  // Obtener campañas de marketplace de una empresa
  getMarketplaceCampaigns: async (companyId, params = {}) => {
    try {
      const response = await api.get(`/marketplace-campaigns/company/${companyId}`, { params })
      return response
    } catch (error) {
      console.error('Error en getMarketplaceCampaigns:', error)
      throw error
    }
  },

  // Obtener estadísticas de campañas de marketplace de una empresa
  getMarketplaceCampaignStats: async (companyId, params = {}) => {
    try {
      const response = await api.get(`/marketplace-campaigns/company/${companyId}/stats`, { params })
      return response
    } catch (error) {
      console.error('Error en getMarketplaceCampaignStats:', error)
      throw error
    }
  },

  // Subir archivo CSV de campañas de marketplace
  uploadMarketplaceCampaignCSV: async (formData) => {
    try {
      // Debug: verificar el token
      const token = localStorage.getItem('authToken')
      logger.debug('Token en upload:', token ? token.substring(0, 30) + '...' : 'null')
      
      // Asegurarnos de que el token se envíe explícitamente
      const headers = {
        'Content-Type': 'multipart/form-data',
      }
      
      if (token) {
        headers.Authorization = `Bearer ${token}`
        logger.debug('Token agregado explícitamente:', token.substring(0, 30) + '...')
      }
      
      const response = await api.post('/marketplace-campaigns/upload', formData, {
        headers,
      })
      return response
    } catch (error) {
      logger.error('Error en uploadMarketplaceCampaignCSV:', error)
      throw error
    }
  },

  // Eliminar una campaña de marketplace
  deleteMarketplaceCampaign: async (campaignId) => {
    try {
      const response = await api.delete(`/marketplace-campaigns/campaign/${campaignId}`)
      return response
    } catch (error) {
      logger.error('Error en deleteMarketplaceCampaign:', error)
      throw error
    }
  },

  // Obtener análisis de performance de campañas
  getCampaignPerformance: async (companyId, params = {}) => {
    try {
      const response = await api.get(`/marketplace-campaigns/performance/${companyId}`, { params })
      return response
    } catch (error) {
      logger.error('Error en getCampaignPerformance:', error)
      throw error
    }
  },

  // Obtener comparativas de campañas (mejores/peores)
  getCampaignComparatives: async (companyId, params = {}) => {
    try {
      const response = await api.get(`/marketplace-campaigns/comparatives/${companyId}`, { params })
      return response
    } catch (error) {
      logger.error('Error en getCampaignComparatives:', error)
      throw error
    }
  },

  // Obtener sugerencias de optimización
  getCampaignOptimization: async (companyId, params = {}) => {
    try {
      const response = await api.get(`/marketplace-campaigns/optimization/${companyId}`, { params })
      return response
    } catch (error) {
      logger.error('Error en getCampaignOptimization:', error)
      throw error
    }
  },

  // Obtener datos de evolución mensual (cruce campañas + cache + datos manuales)
  getCampaignEvolution: async (companyId, params = {}) => {
    try {
      const response = await api.get(`/marketplace-campaigns/evolution/${companyId}`, { params })
      return response
    } catch (error) {
      logger.error('Error en getCampaignEvolution:', error)
      throw error
    }
  },

  // Guardar datos manuales opcionales de evolución
  saveEvolutionManualData: async (companyId, data) => {
    try {
      const response = await api.post(`/marketplace-campaigns/evolution/${companyId}/manual-data`, data)
      return response
    } catch (error) {
      logger.error('Error en saveEvolutionManualData:', error)
      throw error
    }
  },

  // Obtener análisis de presupuesto
  getCampaignBudgetAnalysis: async (companyId, params = {}) => {
    try {
      const response = await api.get(`/marketplace-campaigns/budget-analysis/${companyId}`, { params })
      return response
    } catch (error) {
      logger.error('Error en getCampaignBudgetAnalysis:', error)
      throw error
    }
  },

  // ==================== PROYECCIONES Y FORECASTING ====================

  // Obtener proyecciones de ventas
  getSalesProjection: async (params = {}) => {
    try {
      const response = await api.get('/forecasting/sales-projection', { params })
      return response
    } catch (error) {
      logger.error('Error en getSalesProjection:', error)
      throw error
    }
  },

  // Obtener análisis de estacionalidad
  getSeasonalAnalysis: async (params = {}) => {
    try {
      const response = await api.get('/forecasting/seasonal-analysis', { params })
      return response
    } catch (error) {
      logger.error('Error en getSeasonalAnalysis:', error)
      throw error
    }
  },

  // Obtener predicción de stock
  getStockForecast: async (params = {}) => {
    try {
      const response = await api.get('/forecasting/top-products-forecast', { params })
      return response
    } catch (error) {
      logger.error('Error en getStockForecast:', error)
      throw error
    }
  },

  // Obtener proyección de comisiones
  getCommissionProjection: async (params = {}) => {
    try {
      const response = await api.get('/forecasting/commission-projection', { params })
      return response
    } catch (error) {
      logger.error('Error en getCommissionProjection:', error)
      throw error
    }
  },

  // Obtener proyección de cashflow
  getCashflowProjection: async (params = {}) => {
    try {
      const response = await api.get('/forecasting/cashflow-projection', { params })
      return response
    } catch (error) {
      logger.error('Error en getCashflowProjection:', error)
      throw error
    }
  },

  // ==================== MAPA DE PEDIDOS ====================

  // Obtener datos para el mapa de pedidos
  getMapData: async (params) => {
    try {
      const response = await api.get('/reports/map-data-test', { params })
      return response.data || response
    } catch (error) {
      console.error('Error en getMapData:', error)
      throw error
    }
  },

  // ==================== IA - CHATBOT ====================

  // Consultar al chatbot
  askChatbot: async (chatbotRequest) => {
    try {
      const response = await api.post('/ai/chatbot/ask', chatbotRequest)
      return response.data
    } catch (error) {
      logger.error('Error en askChatbot:', error)
      throw error
    }
  },

  // Estado del chatbot
  getChatbotHealth: async () => {
    try {
      const response = await api.get('/ai/chatbot/health')
      return response.data
    } catch (error) {
      logger.error('Error en getChatbotHealth:', error)
      throw error
    }
  },

  // Estadísticas de uso del chatbot
  getChatbotUsage: async () => {
    try {
      const response = await api.get('/ai/chatbot/usage')
      return response.data
    } catch (error) {
      logger.error('Error en getChatbotUsage:', error)
      throw error
    }
  },

  // ==================== IA - INFORMES ====================

  // Generar informe con IA
  generateAIReport: async (reportRequest) => {
    try {
      const response = await api.post('/ai/reports/generate', reportRequest)
      return response.data
    } catch (error) {
      logger.error('Error en generateAIReport:', error)
      throw error
    }
  },

  // Listar informes generados
  listAIReports: async (params = {}) => {
    try {
      const response = await api.get('/ai/reports/list', { params })
      return response.data
    } catch (error) {
      logger.error('Error en listAIReports:', error)
      throw error
    }
  },

  // Obtener informe específico
  getAIReport: async (reportId) => {
    try {
      const response = await api.get(`/ai/reports/${reportId}`)
      return response.data
    } catch (error) {
      logger.error('Error en getAIReport:', error)
      throw error
    }
  },

  // Eliminar informe
  deleteAIReport: async (reportId) => {
    try {
      const response = await api.delete(`/ai/reports/${reportId}`)
      return response.data
    } catch (error) {
      logger.error('Error en deleteAIReport:', error)
      throw error
    }
  },

  // =============================
  // ALERTAS
  // =============================

  // Generar alertas automáticas
  generateAlerts: async (params) => {
    try {
      const response = await api.post('/alerts/generate', null, { params })
      return response.data || response
    } catch (error) {
      logger.error('Error en generateAlerts:', error)
      throw error
    }
  },

  // Listar alertas
  listAlerts: async (params) => {
    try {
      const response = await api.get('/alerts/list', { params })
      return response.data || response
    } catch (error) {
      logger.error('Error en listAlerts:', error)
      throw error
    }
  },

  // Obtener contador de alertas
  getAlertsCount: async (params) => {
    try {
      const response = await api.get('/alerts/count', { params })
      return response.data || response
    } catch (error) {
      logger.error('Error en getAlertsCount:', error)
      throw error
    }
  },

  // Marcar alerta como leída
  markAlertAsRead: async (alertId) => {
    try {
      const response = await api.patch(`/alerts/${alertId}/mark-read`)
      return response.data || response
    } catch (error) {
      logger.error('Error en markAlertAsRead:', error)
      throw error
    }
  },

  // Descartar alerta
  dismissAlert: async (alertId) => {
    try {
      const response = await api.patch(`/alerts/${alertId}/dismiss`)
      return response.data || response
    } catch (error) {
      logger.error('Error en dismissAlert:', error)
      throw error
    }
  },

  // Eliminar alerta
  deleteAlert: async (alertId) => {
    try {
      const response = await api.delete(`/alerts/${alertId}`)
      return response.data || response
    } catch (error) {
      logger.error('Error en deleteAlert:', error)
      throw error
    }
  },

  // ========== CACHE SYNC ENDPOINTS ==========

  /**
   * Obtiene el dashboard de sincronización de caché
   */
  getCacheDashboard: async () => {
    try {
      const response = await api.get('/cache-admin/dashboard')
      return response.data || response
    } catch (error) {
      logger.error('Error en getCacheDashboard:', error)
      throw error
    }
  },

  /**
   * Sincroniza estadísticas diarias de los últimos N días
   * @param {number} days - Número de días a sincronizar (1-90)
   */
  syncDailyStats: async (days = 7) => {
    try {
      const response = await api.post(`/cache-admin/sync/last-days?days=${days}`)
      return response.data || response
    } catch (error) {
      logger.error('Error en syncDailyStats:', error)
      throw error
    }
  },

  /**
   * Sincroniza productos de los últimos N días
   * @param {number} days - Número de días a sincronizar (1-90)
   */
  syncProducts: async (days = 7) => {
    try {
      const response = await api.post(`/cache-admin/sync/products?days=${days}`)
      return response.data || response
    } catch (error) {
      logger.error('Error en syncProducts:', error)
      throw error
    }
  }
}

export default apiService
export { api }